import loginProps from "common/managers/loginProps";
import typeCast from "common/helper/typeCast";
import console from "common/objects/console";

//ct = client timestamp
//st = server timestamp
//cd = client date
//sd = server date
//md = moment date
class DateConvert {

    getClientTimeZone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    /**
     * Convert a Date object to a long value.
     * @param cdInput
     * @returns {*}
     */
    toLong(cdInput) {
        if (cdInput === null) {
            return null;
        }

        //Ideally the following code could be used
        //This would convert the date to a long that represents 12pm on the specified date in the SERVERS timezone
        //However, because of Mantis #41481, this can't be done right now

        /**
        //Mantis 32953: all dates must be set to 12pm until flex is removed
        var mdOutput = moment.tz({
            year: cdInput.getFullYear(),
            month: cdInput.getMonth(),
            day: cdInput.getDate(),
            hour: 12,
            minute: 0,
            seconds: 0,
            milliseconds: 0
        },this._getTz());
        var stOutput = mdOutput.valueOf();
        return stOutput;
         */

        // There are scenarios in job view prompts and in search prompts where the same value is being used by the server
        // to compare against both a moment in time and date value.
        // In order to support that use case, all dates will still be sent at 12pm in the client's timezone to the server
        // This works for moment in time values only if the server is aware of the 12pm hack and accounts for it
        // This works for dates only if server and client are within 12 hours of each other
        // Alternatives:
        // 1) Search prompts and job views should send both a client timezone converted value and a server timezone converted value
        // 2) Search prompts and job view prompts should be redesigned to support explicit datetime prompts

        var cdClone = new Date(cdInput.getTime());
        cdClone.setHours(12);
        cdClone.setMinutes(0);
        cdClone.setSeconds(0);
        cdClone.setMilliseconds(0);
        return cdClone.getTime();
    }

    /**
     * Convert a Date input into a system neutral string.
     * @param cdInput
     * @returns YYYY-MM-DD
     */
    toSystemNeutral(cdInput) {
        if (cdInput === null) {
            return null;
        }
        if (!(cdInput instanceof Date)) {
            //handle long as the only special case
            cdInput = new Date(kendo.parseFloat(cdInput));
        }
        return kendo.toString(cdInput, "yyyy-MM-dd");
    }

    /**
     * Convert a long value to a Date object.  If input is not a valid long, null is returned.
     * @param stInput
     * @returns {Date}
     */
    toDate(stInput) {
        const d = this.toDateUsingClientsTimeZone(stInput);
        if(d === null) {
            return null;
        }
        let format;
        try {
            format = new Intl.DateTimeFormat("en-US", {
                timeZone: loginProps.get().serverTimeZoneId,
                year: "numeric",
                month: "numeric",
                day: "numeric"
            });
        } catch {
            format = new Intl.DateTimeFormat("en-US", {
                timeZone: "UTC",
                year: "numeric",
                month: "numeric",
                day: "numeric"
            });
        }
        const parts = format.formatToParts(d);
        const get = type => parts.filter(p => p.type === type).map(p => p.value)[0];
        //Mantis 32953: all dates use 12pm until flex is removed
        return new Date(get("year"), get("month") - 1, get("day"), 12, 0, 0, 0);
    }

    /**
     * Convert a long value to a Date object in the client's time zone.
     *
     * @param millis date encoded as a long of milliseconds
     * @return {Date}
     */
    toDateUsingClientsTimeZone(millis) {
        if (millis instanceof Date) {
            return millis;
        } else if (millis == "" || millis === null || millis === undefined || isNaN(millis)) {
            return null
        } else {
            return new Date(kendo.parseFloat(millis));
        }
    }

    /**
     * Convert a date to a long value in the client's time zone.
     *
     * @param date date
     * @return {number} date encoded as a long of milliseconds
     */
    toLongUsingClientsTimeZone(date) {
        if (date === null) {
            return null;
        } else {
            return date.getTime();
        }
    }

    /**
     * Convert a string in the form of "YYYY-MM-DD" to a date object.
     * Ignores characters after the first 10.
     * @param neutral
     * @returns {Date} return date if format can be converted, null otherwise.
     */
    neutralToDate(neutral) {
        if (neutral && typeof neutral == "string") {
            neutral = neutral.substring(0, 10);
            var parts = neutral.split("-");
            if (parts.length != 3) {
                return null;
            }
            return new Date(parts[0], parts[1] - 1, parts[2]);
        }
        return null;
    }

    /**
     * Converts a string in the form of YYYY-MM-DD or a long into a long.
     * @param neutral Neutral or long
     * @returns {*} Long value
     */
    neutralToLong(neutral) {
        var date = this.neutralToDate(neutral);
        if (date === null) {
            date = this.toDate(neutral);
        }
        if (date !== null) {
            return this.toLong(date);
        } else {
            return null;
        }
    }

    convertFromMilitaryTime(value) {
        if (value) {
            var hours;
            var minutes;
            if (value instanceof Date) {
                hours = value.getHours();
                minutes = value.getMinutes();
            } else {
                var parts = value.split(":");
                hours = parseInt(parts[0]);
                minutes = parseInt(parts[1]);
            }
            var suffix = (hours < 12) ? "AM" : "PM";
            if (hours > 12) {
                hours -= 12;
            }
            minutes = (minutes < 10) ? "0" + minutes : "" + minutes;
            value = hours + ":" + minutes + " " + suffix;
        }
        return value;
    }

    convertToMilitaryTime(value) {
        if (value) {
            var hours;
            var minutes;
            if (value instanceof Date) {
                hours = value.getHours();
                minutes = value.getMinutes();
            } else {
                var parts = value.split(":");
                hours = parseInt(parts[0]);
                parts = parts[1].split(" ");
                minutes = parseInt(parts[0]);
                var suffix = parts[1].toUpperCase();
                if (suffix == "PM" && hours < 12) {
                    hours += 12;
                }
            }
            minutes = (minutes < 10) ? "0" + minutes : "" + minutes;
            hours = (hours < 10) ? "0" + hours : "" + hours;
            value = hours + ":" + minutes;
        }
        return value;
    }

}

export default new DateConvert();
